import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FishGroupsController_updateMetadata } from '@/services/services';

import type { MutationState } from '../shared/mutation';
import { sendWireBody } from '../shared/wireRequest';

import { invalidateFishGroupQueries } from './_invalidateQueries';
import type { UpdateFishGroupMetadataRequest } from './types';

interface MetadataUpdate {
    clientId: number;
    fishGroupId: number;
    requestBody: UpdateFishGroupMetadataRequest;
}

function updateFishGroupMetadata({ clientId, fishGroupId, requestBody }: MetadataUpdate) {
    return sendWireBody(
        'patchRequest',
        FishGroupsController_updateMetadata,
        { clientId, fishGroupId },
        requestBody
    );
}

export interface UseUpdateFishGroupMetadataResult extends MutationState {
    updateMetadata: (requestBody: UpdateFishGroupMetadataRequest) => Promise<void>;
}

interface UseUpdateFishGroupMetadataOptions {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

/**
 * Correct a fish group's metadata (name, notes, initialPopulationSize).
 *
 * Omitting a field leaves it untouched; passing `null` clears it — so callers
 * must send only the fields the operator actually edited, never a full snapshot
 * of the form.
 *
 * Invalidation covers the events query too, so the new `metadata_updated`
 * entry appears in the Events tab without a manual refetch.
 */
export const useUpdateFishGroupMetadata = (
    clientId: number,
    fishGroupId: number,
    options?: UseUpdateFishGroupMetadataOptions
): UseUpdateFishGroupMetadataResult => {
    const queryClient = useQueryClient();
    const mutation = useMutation({ mutationFn: updateFishGroupMetadata });

    const updateMetadata = async (requestBody: UpdateFishGroupMetadataRequest): Promise<void> => {
        try {
            await mutation.mutateAsync({
                clientId,
                fishGroupId,
                requestBody,
            });
            await invalidateFishGroupQueries(queryClient, clientId, fishGroupId);
            options?.onSuccess?.();
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        updateMetadata,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
